/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { MAX_SUBNAV_COLUMNS } from '@/lib/navigation/columns';
import type { NavCategory } from '@/lib/navigation/types';
import { SubnavGroups } from '@/components/navigation/subnav-groups';

/**
 * The regular subnav (SCO-NAV-04): level 2 headings grouping level 3 links, with the
 * level 2 content component as the right-hand column.
 */
export function SubnavPanel({
    groups,
    contentSlot,
    onNavigate,
}: {
    groups: NavCategory[];
    contentSlot?: ReactNode;
    onNavigate?: () => void;
}): ReactElement {
    // SCO-NAV-03 D: without a content component the categories get that column back.
    const columns = contentSlot ? MAX_SUBNAV_COLUMNS - 1 : MAX_SUBNAV_COLUMNS;

    return (
        // The scroll container (SCO-483): max-height and overflow-y live here rather than on
        // the Radix Viewport, so the bar never lands on the node Radix measures.
        <div
            className="overflow-x-hidden overflow-y-auto bg-secondary"
            style={{ maxHeight: 'calc(100dvh - var(--header-height))' }}>
            <div className="bg-secondary">
                <div className="section-container flex items-start gap-8">
                    <SubnavGroups
                        groups={groups}
                        columns={columns}
                        className="min-w-px flex-1 py-10"
                        onNavigate={onNavigate}
                    />
                    {contentSlot ? <div className="flex shrink-0 items-center py-10">{contentSlot}</div> : null}
                </div>
            </div>
        </div>
    );
}
